/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { getCluProjectService } from 'src/clu/core/services/projects/cluProjectService';
import {
    CluCopyProjectSwagger,
    CluProjectDataSwagger,
    CluProjectImportFormat,
    CluProjectImportProjectSwagger,
    CluProjectType
} from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluIntentStore } from 'src/clu/core/stores/CluIntentStore';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { cluEntitySwaggerToCluEntityStore } from 'src/clu/modules/handlers/entities/cluEntityMappers';
import { addCluIntentsHelper } from 'src/clu/modules/handlers/intents/cluIntentHandlerGroup';
import { cluIntentSwaggerToCluIntentStore } from 'src/clu/modules/handlers/intents/cluIntentMappers';
import {
    cluProjectCultureSwaggerToCultureStore,
    cluProjectSwaggerToCluProjectStore
} from 'src/clu/modules/handlers/projects/cluProjectMappers';
import { loadCluTrainedModelsHelper } from 'src/clu/modules/handlers/trainedModels/cluTrainedModelHandlerGroup';
import { fileService } from 'src/core/services/api/fileService';
import { getAzureService } from 'src/core/services/azure/azureService';
import { getCustomSkillGlobalsService } from 'src/core/services/customSkillGlobalService';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { CustomSkillGlobalsStore } from 'src/core/stores/CustomSkillGlobalsStore';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { batchPromises, getNextAbleResults } from 'src/core/utils/batchPromises';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TrainingConfigVersionSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';
import { TrainingConfigVersion } from 'src/modules/modelTraining/types/trainingTypes';

export type CluProjectHandlerGroup = ReturnType<typeof createCluProjectHandlerGroup>;

const loadCluCultures = async (rootStore: RootStore) => {
    const { cluConversationCultureStoreHouse, cluOrchestrationCultureStoreHouse } = rootStore;

    if (
        cluConversationCultureStoreHouse.tracker.isRunning ||
        cluConversationCultureStoreHouse.tracker.isCompleted ||
        cluOrchestrationCultureStoreHouse.tracker.isRunning ||
        cluOrchestrationCultureStoreHouse.tracker.isCompleted
    ) {
        return;
    }

    try {
        cluConversationCultureStoreHouse.tracker.setState('running');
        cluOrchestrationCultureStoreHouse.tracker.setState('running');

        const [conversationCulturesSwaggers, orchestrationCulturesSwaggers] = await Promise.all([
            getNextAbleResults(() => getCustomSkillGlobalsService().getProjectCultures('Conversation'), 'value'),
            getNextAbleResults(() => getCustomSkillGlobalsService().getProjectCultures('Orchestration'), 'value')
        ]);

        const conversationCultureStores = conversationCulturesSwaggers.map(cluProjectCultureSwaggerToCultureStore);
        const orchestrationCultureStores = orchestrationCulturesSwaggers.map(cluProjectCultureSwaggerToCultureStore);

        transaction(() => {
            cluConversationCultureStoreHouse.setItems(conversationCultureStores);
            cluOrchestrationCultureStoreHouse.setItems(orchestrationCultureStores);

            cluConversationCultureStoreHouse.tracker.setState('completed');
            cluOrchestrationCultureStoreHouse.tracker.setState('completed');
        });
    } catch (e) {
        cluConversationCultureStoreHouse.tracker.setState('failed', <Error>e);
        cluOrchestrationCultureStoreHouse.tracker.setState('failed', <Error>e);

        throw e;
    }
};

export const createCluProjectHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, uiStore } = dependencies;

    const getCluProjectTrainingConfigVersions = async (projectKind: CluProjectType) => {
        const { cluGlobalsStoreHouse } = rootStore;

        let globalsStore = cluGlobalsStoreHouse.items.find(i => i.id === projectKind);
        if (!globalsStore) {
            globalsStore = new CustomSkillGlobalsStore(projectKind);
            cluGlobalsStoreHouse.add(globalsStore);
        }

        if (!globalsStore.trainingConfigVersions?.length) {
            const results = await getNextAbleResults<'value', TrainingConfigVersionSwagger>(
                (nextLink: string) => getCustomSkillGlobalsService().getTrainingConfigVersions(projectKind, nextLink),
                'value'
            );
            globalsStore.setTrainingConfigVersions(
                results.map(
                    r =>
                        <TrainingConfigVersion>{
                            modelExpirationDate: r?.modelExpirationDate ? new Date(r.modelExpirationDate) : undefined,
                            trainingConfigVersion: r.trainingConfigVersion,
                            isLatest: r.isLatest
                        }
                )
            );
        }
    };

    const loadCluProjects = async (_?: HandlerParams<object>) => {
        const { cluProjectStoreHouse: cluProjectsStoreHouse } = rootStore;

        try {
            cluProjectsStoreHouse.tracker.setState('running');

            const projectSwaggers = await getNextAbleResults(getCluProjectService().getProjects, 'value');
            await loadCluCultures(rootStore);

            const projectStores = projectSwaggers
                .map(cluProjectSwaggerToCluProjectStore)
                .filter(p => p.projectType === 'Conversation' || p.projectType === 'Orchestration');

            transaction(() => {
                cluProjectsStoreHouse.setItems(projectStores);
                cluProjectsStoreHouse.tracker.setState('completed');
            });
        } catch (err) {
            cluProjectsStoreHouse.setItems([]);
            cluProjectsStoreHouse.tracker.setState('failed', <Error>err);
        }
    };

    const openCluProject = async ({ projectName }: HandlerParams<{ projectName: string }>) => {
        const projectService = getCluProjectService();
        const projectSwagger = await projectService.getProject({ projectName });

        if (
            !rootStore.cluConversationCultureStoreHouse.tracker.isCompleted ||
            !rootStore.cluOrchestrationCultureStoreHouse.tracker.isCompleted
        ) {
            await loadCluCultures(rootStore);
        }

        await getCluProjectTrainingConfigVersions(projectSwagger.projectKind);

        let projectAssetsSwagger = (await projectService.getProjectData({ projectName })).assets;
        // Backend returns empty arrays as null. This is to counteract that until they fix it.
        projectAssetsSwagger = projectAssetsSwagger ?? {
            projectKind: projectSwagger.projectKind,
            intents: [],
            entities: [],
            utterances: []
        };
        projectAssetsSwagger.intents = projectAssetsSwagger.intents ?? [];
        projectAssetsSwagger.entities = projectAssetsSwagger.entities ?? [];
        projectAssetsSwagger.utterances = projectAssetsSwagger.utterances ?? [];
        await transaction(async () => {
            const projectStore = cluProjectSwaggerToCluProjectStore(projectSwagger);
            projectStore.setCsAccountPath(rootStore.azureStore.session.csAccountStore?.path);
            projectStore.patchJsonData(projectAssetsSwagger);
            projectStore.intentStoreHouse.batchAdd(projectAssetsSwagger.intents.map(cluIntentSwaggerToCluIntentStore));
            projectStore.entityStoreHouse.batchAdd(projectAssetsSwagger.entities.map(cluEntitySwaggerToCluEntityStore));
            projectStore.intentStoreHouse.tracker.setState('completed');
            projectStore.entityStoreHouse.tracker.setState('completed');

            rootStore.cluProjectStoreHouse.setItems([projectStore]);
            rootStore.setCurrentCluProjectStore(projectStore);

            await loadCluTrainedModelsHelper(rootStore);
        });
    };

    const closeCluProject = async () => {
        rootStore.setCurrentCluProjectStore(null);
        uiStore.setCluBuildSchemaPageActivePivot('intentPivot');
    };

    const createCluProject = async ({
        name,
        culture,
        projectType,
        description,
        isMultiLingual,
        connectedIntents
    }: HandlerParams<
        Pick<CluProjectStore, 'name' | 'description' | 'culture' | 'projectType' | 'isMultiLingual'> & {
            connectedIntents?: Pick<CluIntentStore, 'name' | 'orchestrationInfo'>[];
        }
    >) => {
        await getCluProjectService().createProject({
            description,
            language: culture,
            projectName: name,
            projectKind: projectType,
            multilingual: isMultiLingual,
            settings: { confidenceThreshold: 0, normalizeCasing: false, augmentDiacritics: false }
        });

        const projectStore = new CluProjectStore(name, {
            culture,
            projectType,
            description,
            isMultiLingual,
            oosThreshold: 0,
            isCasingNormalized: false,
            isDiacriticsAugmented: false,
            createdDate: new Date(),
            modifiedDate: new Date(),
            csAccountPath: rootStore.azureStore.session.csAccountStore.path
        });

        await transaction(async () => {
            rootStore.cluProjectStoreHouse.add(projectStore);

            if (connectedIntents?.length) {
                await addCluIntentsHelper(connectedIntents, projectStore);
            }
        });
    };

    const importCluProject = async ({ file, newProjectName, tracker }: HandlerParams<{ file: File; newProjectName?: string }>) => {
        let fileContent = await fileService.readFromDisk(file);
        let currentProjectName: string;
        let format: CluProjectImportFormat;

        try {
            let projectSwagger = <CluProjectImportProjectSwagger>JSON.parse(fileContent);
            if ((<CluProjectDataSwagger>projectSwagger).metadata) {
                currentProjectName =
                    (<CluProjectDataSwagger>projectSwagger).metadata.projectName ??
                    (<{ metadata: { name: string } }>projectSwagger).metadata?.name;
                if (newProjectName) {
                    if ((<CluProjectDataSwagger>projectSwagger).metadata.projectName) {
                        projectSwagger = <CluProjectDataSwagger>{
                            ...projectSwagger,
                            metadata: { ...(<CluProjectDataSwagger>projectSwagger).metadata, projectName: newProjectName }
                        };
                    } else {
                        projectSwagger = {
                            ...projectSwagger,
                            metadata: { ...(<{ metadata: { name: string } }>projectSwagger).metadata, name: newProjectName }
                        };
                    }
                }
                fileContent = JSON.stringify(projectSwagger);
                format = 'Conversation';
            } else {
                format = 'Luis';
                const fileContentInJson = <{ name: string }>JSON.parse(fileContent);
                currentProjectName = fileContentInJson.name;
                if (newProjectName) {
                    fileContentInJson.name = newProjectName;
                }
                fileContent = JSON.stringify(fileContentInJson);
            }
        } catch {
            const error = new Error();
            error.name = 'Invalid format';
            error.message = 'The file provided is of invalid JSON format.';

            throw error;
        }

        const projectName = newProjectName || currentProjectName;

        await getCluProjectService().importProject({ projectName, fileContent, format });

        if (tracker) {
            tracker.setContext(projectName);
        }
    };

    const updateCluProject = async ({
        description,
        isMultiLingual,
        oosThreshold,
        isCasingNormalized,
        isDiacriticsAugmented }:
    HandlerParams<
        Pick<CluProjectStore, 'description' | 'isMultiLingual' | 'oosThreshold' | 'isCasingNormalized' | 'isDiacriticsAugmented' >
    >) => {
        const projectStore = rootStore.currentCluProjectStore;

        await getCluProjectService().updateProject({
            description,
            multilingual: isMultiLingual,
            projectName: projectStore.name,
            language: projectStore.culture,
            projectKind: projectStore.projectType,
            settings: {
                confidenceThreshold: oosThreshold,
                normalizeCasing: isCasingNormalized,
                augmentDiacritics: isDiacriticsAugmented
            }
        });

        projectStore.setDescription(description);
        projectStore.setIsMultiLingual(isMultiLingual);
        projectStore.setOosThreshold(oosThreshold);
        projectStore.setIsCasingNormalized(isCasingNormalized);
        projectStore.setIsDiacriticsAugmented(isDiacriticsAugmented);
    };

    const copyCluProject = async ({
        projectName,
        projectKind,
        allowOverwrite,
        targetResource
    }: HandlerParams<{
        projectName: string;
        allowOverwrite: boolean;
        projectKind: CluProjectType;
        targetResource: AzureCsAccountStore;
    }>) => {
        let keys = targetResource.keyStrings;

        if (!keys?.key1) {
            const tenantToken = rootStore.azureStore.session.userTokens.armToken;
            keys = await getAzureService().getAzureCsAccountKeyStringsByPath({ tenantToken, path: targetResource.path });
        }

        const authorizeCopyResponse: CluCopyProjectSwagger = await getCluProjectService().authorizeCopyProject({
            projectKind,
            projectName,
            allowOverwrite,
            serviceKey: keys.key1,
            resourceId: targetResource.id,
            targetResourceEndpoint: targetResource.endpoint
        });

        await getCluProjectService().copyProject({
            copyProjectSwagger: authorizeCopyResponse,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: data => {
                    if (data.status === 'failed') {
                        throw new Error(data.errors?.[0]?.message);
                    }
                }
            }
        });
    };

    const deleteCluProjects = async ({
        projectNames,
        appsLocalStoreHouse,
        generateMultiFailureMessage
    }: HandlerParams<{
        projectNames: string[];
        appsLocalStoreHouse?: ResourceStoreHouse<AppStore>;
        generateMultiFailureMessage: (failedCount: number) => string;
    }>) => {
        const { cluProjectStoreHouse } = rootStore;
        const storeHouse = appsLocalStoreHouse || cluProjectStoreHouse;
        const service = getCluProjectService();

        storeHouse.tracker.setState('running');

        const { results, failedCount } = await batchPromises(
            projectNames.map(projectName =>
                service.deleteProject({
                    projectName,
                    operationHandle: {
                        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                        retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                        handleCall: data => {
                            if (data.status === 'failed') {
                                throw new Error(data.errors?.[0]?.message);
                            }
                        }
                    }
                })
            )
        );

        const succeededIds = results.filter(res => res.status === 'success').map((_, index) => projectNames[index]);

        if (failedCount > 0) {
            throw new Error(generateMultiFailureMessage(failedCount));
        }

        transaction(() => {
            storeHouse.batchRemoveByIds(succeededIds);
            storeHouse.tracker.setState('completed');
        });
    };

    const exportCluProject = async ({
        projectName,
        trainedModelName
    }: HandlerParams<{ projectName: string; trainedModelName?: string }>) => {
        await getCluProjectService().exportProject({ projectName, trainedModelName });
    };

    return {
        copyCluProject,
        openCluProject,
        closeCluProject,
        loadCluProjects,
        importCluProject,
        createCluProject,
        updateCluProject,
        exportCluProject,
        deleteCluProjects
    };
};
